import { Icon } from '@iconify/react';
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

// Same look as the app-wide `global-tooltip` in src/App.tsx
const TOUCH_TOOLTIP_STYLE = {
  backgroundColor: 'black',
  color: 'white',
  zIndex: 99999,
  maxWidth: '80%',
  wordWrap: 'break-word',
} as const;

/**
 * Info icon in the "Shopping List Last Purchased" grid column header (cart, catalog, shopping
 * list). The header's `.k-link` sorts on click, so the icon must swallow its own clicks. Touch has
 * no hover: a tap opens the tooltip and it stays open until the next tap anywhere else (same model
 * as design-system PopUp).
 */
const LastPurchasedInfoIcon = () => {
  const { t } = useTranslation();
  const anchorKey = useId();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [isTouchOpen, setIsTouchOpen] = useState(false);
  const tooltipText = t('shoppingListLastPurchasedTooltip');

  useEffect(() => {
    if (!isTouchOpen) {
      return undefined;
    }

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (anchorRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsTouchOpen(false);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
  }, [isTouchOpen]);

  // Mouse click on the icon must not sort the column
  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  // Enter / Space on the focused icon must not reach the header's keyboard sort
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  // Cancelling touchend drops the emulated hover + click, so the tap neither sorts nor flashes
  // the header-title tooltip; the tap toggles the touch tooltip instead.
  const handleTouchEnd = (event: ReactTouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsTouchOpen((open) => !open);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label={tooltipText}
        data-last-purchased-info={anchorKey}
        data-tooltip-id="global-tooltip"
        data-tooltip-content={tooltipText}
        // ~25px tap target; negative margins keep the header layout. No right margin: .k-link clips it.
        className="-my-1.5 -ml-1.5 inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-1.5 text-inherit"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onTouchEnd={handleTouchEnd}
      >
        <Icon icon="mdi:information-outline" width="13" height="13" />
      </button>
      {/* Portal: the header's .k-link, th and header wrapper all hide overflow and would clip it */}
      {createPortal(
        <Tooltip
          anchorSelect={`[data-last-purchased-info="${anchorKey}"]`}
          isOpen={isTouchOpen}
          content={tooltipText}
          place="bottom"
          style={TOUCH_TOOLTIP_STYLE}
        />,
        document.body
      )}
    </>
  );
};

export default LastPurchasedInfoIcon;
